<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="700"
    persistent
    scrollable
    @click:outside="closeIfNotTesting"
  >
    <v-card class="carrier-test-dialog">
      <!-- Header -->
      <v-card-title class="dialog-header pa-4">
        <v-icon left color="white">mdi-lan-connect</v-icon>
        <span class="white--text">Carrier Connection Test</span>
        <v-spacer></v-spacer>
        <v-btn icon dark small :disabled="isTesting" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Tabs -->
      <v-tabs v-model="activeTab" background-color="white" color="primary" grow>
        <v-tab key="test">
          <v-icon left small>mdi-play-circle-outline</v-icon>
          Run Test
        </v-tab>
        <v-tab key="history">
          <v-icon left small>mdi-history</v-icon>
          Test History
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text class="pa-0" style="max-height: 500px; overflow-y: auto;">
        <v-tabs-items v-model="activeTab">
          <!-- Test Tab -->
          <v-tab-item key="test">
            <div class="pa-4">
              <!-- Carrier Info -->
              <v-alert
                v-if="carrierInfo"
                type="info"
                dense
                outlined
                class="mb-4"
              >
                <div class="d-flex align-center">
                  <v-icon left small>mdi-server</v-icon>
                  <span>
                    Testing: <strong>{{ carrierInfo.host }}</strong
                    >:{{ carrierInfo.port || 5060 }}
                    <span v-if="carrierInfo.username">
                      ({{ carrierInfo.username }})
                    </span>
                  </span>
                </div>
              </v-alert>

              <!-- No Carrier Warning -->
              <v-alert
                v-else-if="!isTesting && !testResult"
                type="warning"
                dense
                outlined
                class="mb-4"
              >
                <v-icon left>mdi-alert</v-icon>
                No carrier configured. Please configure a SIP carrier first.
              </v-alert>

              <!-- Test Steps -->
              <div class="test-steps">
                <v-timeline dense align-top>
                  <v-timeline-item
                    v-for="step in displaySteps"
                    :key="step.step"
                    :color="getStepColor(step.status)"
                    :icon="getStepIcon(step.status)"
                    fill-dot
                    small
                  >
                    <div class="step-content">
                      <div class="d-flex align-center mb-1">
                        <span class="step-title font-weight-medium">{{
                          step.name
                        }}</span>
                        <v-spacer></v-spacer>
                        <v-chip
                          v-if="step.status !== 'pending'"
                          x-small
                          :color="getStepColor(step.status)"
                          :text-color="getStepTextColor(step.status)"
                        >
                          {{ step.status.toUpperCase() }}
                        </v-chip>
                        <v-progress-circular
                          v-else
                          indeterminate
                          color="primary"
                          size="16"
                          width="2"
                        ></v-progress-circular>
                      </div>
                      <div class="step-message grey--text text--darken-1">
                        {{ step.message || "Waiting..." }}
                      </div>
                      <div
                        v-if="step.duration && step.status !== 'pending'"
                        class="step-duration caption grey--text"
                      >
                        Duration: {{ step.duration }}ms
                      </div>

                      <!-- Expandable Details -->
                      <v-expand-transition>
                        <div
                          v-if="
                            step.details &&
                              Object.keys(step.details).length > 0 &&
                              expandedSteps.includes(step.step)
                          "
                          class="step-details mt-2 pa-2 grey lighten-4 rounded"
                        >
                          <pre class="caption">{{
                            JSON.stringify(step.details, null, 2)
                          }}</pre>
                        </div>
                      </v-expand-transition>

                      <v-btn
                        v-if="
                          step.details && Object.keys(step.details).length > 0
                        "
                        x-small
                        text
                        color="primary"
                        class="mt-1"
                        @click="toggleStepDetails(step.step)"
                      >
                        {{
                          expandedSteps.includes(step.step) ? "Hide" : "Show"
                        }}
                        Details
                      </v-btn>
                    </div>
                  </v-timeline-item>
                </v-timeline>
              </div>

              <!-- Auth Warning Alert (shows when test passes but auth failed) -->
              <v-alert
                v-if="testResult && !isTesting && testResult.authWarning"
                type="warning"
                prominent
                class="mt-4"
              >
                <v-row align="center">
                  <v-col class="grow">
                    <div class="text-h6">
                      <v-icon left color="warning">mdi-alert</v-icon>
                      Authentication Warning
                    </div>
                    <div class="mt-2">
                      The SIP carrier is reachable, but the username/password
                      could not be verified.
                      <strong
                        >Outbound calls may fail if the credentials are
                        incorrect.</strong
                      >
                    </div>
                    <div class="caption mt-2">
                      Please double-check your username and password in the
                      carrier settings.
                    </div>
                  </v-col>
                </v-row>
              </v-alert>

              <!-- Overall Result -->
              <v-alert
                v-if="testResult && !isTesting"
                :type="getOverallAlertType()"
                prominent
                class="mt-4"
              >
                <v-row align="center">
                  <v-col class="grow">
                    <div class="text-h6">
                      {{
                        testResult.success
                          ? testResult.authWarning
                            ? "⚠ Connection Test Passed with Warnings"
                            : "✓ Connection Test Passed"
                          : "✗ Connection Test Failed"
                      }}
                    </div>
                    <div v-if="!testResult.success && getFailureReason()">
                      {{ getFailureReason() }}
                    </div>
                    <div class="caption mt-1">
                      Total Duration: {{ testResult.totalDuration || 0 }}ms
                    </div>
                  </v-col>
                  <v-col class="shrink">
                    <v-btn outlined :disabled="isTesting" @click="runTest">
                      <v-icon left>mdi-refresh</v-icon>
                      Re-run Test
                    </v-btn>
                  </v-col>
                </v-row>
              </v-alert>
            </div>
          </v-tab-item>

          <!-- History Tab -->
          <v-tab-item key="history">
            <div class="pa-4">
              <v-btn
                small
                outlined
                color="primary"
                class="mb-4"
                :loading="loadingHistory"
                @click="loadHistory"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Refresh History
              </v-btn>

              <v-alert v-if="historyError" type="error" dense outlined>
                {{ historyError }}
              </v-alert>

              <div v-if="testHistory.length === 0 && !loadingHistory">
                <v-alert type="info" dense outlined>
                  No test history found. Run a connection test to see history.
                </v-alert>
              </div>

              <v-list v-else two-line>
                <div v-for="(log, index) in testHistory" :key="log.id">
                  <v-list-item @click="showHistoryDetail(log)">
                    <v-list-item-avatar>
                      <v-icon
                        :color="log.success ? 'success' : 'error'"
                        size="28"
                      >
                        {{
                          log.success ? "mdi-check-circle" : "mdi-close-circle"
                        }}
                      </v-icon>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title>
                        {{ log.carrierHost }}:{{ log.carrierPort || 5060 }}
                        <v-chip
                          x-small
                          :color="log.success ? 'success' : 'error'"
                          class="ml-2"
                        >
                          {{ log.success ? "PASSED" : "FAILED" }}
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ formatDate(log.createdAt) }}
                        <span v-if="log.totalDuration">
                          · {{ log.totalDuration }}ms
                        </span>
                        <span v-if="!log.success && log.failureReason">
                          · {{ log.failureReason }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-icon small>mdi-chevron-right</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider v-if="index < testHistory.length - 1"></v-divider>
                </div>
              </v-list>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <!-- Actions -->
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text :disabled="isTesting" @click="closeDialog"> Close </v-btn>
        <v-btn
          color="primary"
          :loading="isTesting"
          :disabled="isTesting || !carrierInfo"
          @click="runTest"
        >
          <v-icon left>mdi-play</v-icon>
          {{ isTesting ? "Testing..." : "Run Connection Test" }}
        </v-btn>
      </v-card-actions>

      <!-- History Detail Dialog -->
      <v-dialog v-model="historyDetailDialog" max-width="600">
        <v-card v-if="selectedHistoryLog">
          <v-card-title class="headline">
            Test Details
            <v-spacer></v-spacer>
            <v-btn icon @click="historyDetailDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td class="font-weight-medium">Status</td>
                  <td>
                    <v-chip
                      small
                      :color="selectedHistoryLog.success ? 'success' : 'error'"
                    >
                      {{ selectedHistoryLog.success ? "PASSED" : "FAILED" }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Host</td>
                  <td>
                    {{ selectedHistoryLog.carrierHost }}:{{
                      selectedHistoryLog.carrierPort
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Date</td>
                  <td>{{ formatDate(selectedHistoryLog.createdAt) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Duration</td>
                  <td>{{ selectedHistoryLog.totalDuration }}ms</td>
                </tr>
                <tr v-if="selectedHistoryLog.failureReason">
                  <td class="font-weight-medium">Failure Reason</td>
                  <td class="error--text">
                    {{ selectedHistoryLog.failureReason }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-divider class="my-3"></v-divider>

            <div class="text-subtitle-2 mb-2">Test Steps</div>
            <v-timeline dense align-top>
              <v-timeline-item
                v-for="step in selectedHistoryLog.testSteps || []"
                :key="step.step"
                :color="getStepColor(step.status)"
                :icon="getStepIcon(step.status)"
                fill-dot
                small
              >
                <div>
                  <div class="font-weight-medium">{{ step.name }}</div>
                  <div class="grey--text">{{ step.message }}</div>
                  <div v-if="step.duration" class="caption grey--text">
                    {{ step.duration }}ms
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "CarrierConnectionTestDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      activeTab: 0,
      isTesting: false,
      testResult: null,
      displaySteps: [],
      expandedSteps: [],
      carrierInfo: null,
      testHistory: [],
      loadingHistory: false,
      historyError: null,
      historyDetailDialog: false,
      selectedHistoryLog: null,
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.loadCarrierInfo();
        this.loadHistory();
      }
    },
  },

  methods: {
    async loadCarrierInfo() {
      try {
        const baseUrl = this.$store.state.ENDPOINT || "";
        const response = await this.$http.get(`${baseUrl}/pbx`, {
          params: {
            filter: JSON.stringify({ where: { companyId: this.companyId } }),
          },
        });

        const pbxConfigs = response.body;
        const pbxConfig =
          pbxConfigs && pbxConfigs.length > 0 ? pbxConfigs[0] : null;

        if (pbxConfig && pbxConfig.carrier && pbxConfig.carrier.host) {
          this.carrierInfo = pbxConfig.carrier;
        } else {
          this.carrierInfo = null;
        }
      } catch (error) {
        console.error("Error loading carrier info:", error);
        this.carrierInfo = null;
      }
    },

    async runTest() {
      if (!this.carrierInfo) {
        this.$swal({
          title: "No Carrier",
          text: "Please configure a SIP carrier first.",
          icon: "warning",
        });
        return;
      }

      this.isTesting = true;
      this.testResult = null;
      this.expandedSteps = [];
      this.activeTab = 0;

      // Initialize pending steps
      this.displaySteps = [
        {
          step: 1,
          name: "Network Resolution",
          status: "pending",
          message: "Checking DNS/IP resolution...",
        },
        {
          step: 2,
          name: "Port Connectivity",
          status: "pending",
          message: "Testing port connectivity...",
        },
        {
          step: 3,
          name: "SIP OPTIONS Probe",
          status: "pending",
          message: "Sending SIP OPTIONS request...",
        },
        {
          step: 4,
          name: "SIP Authentication",
          status: "pending",
          message: "Testing REGISTER authentication...",
        },
        {
          step: 5,
          name: "Overall Assessment",
          status: "pending",
          message: "Evaluating results...",
        },
      ];

      try {
        const baseUrl = this.$store.state.ENDPOINT || "";
        const user = this.$store.state.user;
        const accountsId = user && user.id ? user.id : null;

        const response = await this.$http.post(
          `${baseUrl}/pbx/test-connection-full`,
          {
            companyId: this.companyId,
            accountsId: accountsId,
          }
        );

        this.testResult = response.body;

        // Update display steps with actual results
        if (response.body.steps) {
          this.displaySteps = response.body.steps;
        }

        // Refresh history after test
        this.loadHistory();
      } catch (error) {
        console.error("Connection test error:", error);
        this.testResult = {
          success: false,
          steps: [
            {
              step: 1,
              name: "API Error",
              status: "failed",
              message:
                (error.response &&
                  error.response.data &&
                  error.response.data.error &&
                  error.response.data.error.message) ||
                error.message,
            },
          ],
          totalDuration: 0,
        };
        this.displaySteps = this.testResult.steps;
      } finally {
        this.isTesting = false;
      }
    },

    async loadHistory() {
      this.loadingHistory = true;
      this.historyError = null;

      try {
        const baseUrl = this.$store.state.ENDPOINT || "";
        const response = await this.$http.get(
          `${baseUrl}/pbx/connection-test-history`,
          {
            params: {
              companyId: this.companyId,
              limit: 20,
            },
          }
        );
        this.testHistory = response.body || [];
      } catch (error) {
        console.error("Error loading history:", error);
        this.historyError =
          (error.body && error.body.error && error.body.error.message) ||
          "Failed to load history";
        this.testHistory = [];
      } finally {
        this.loadingHistory = false;
      }
    },

    showHistoryDetail(log) {
      this.selectedHistoryLog = log;
      this.historyDetailDialog = true;
    },

    getStepColor(status) {
      const colors = {
        success: "success",
        failed: "error",
        warning: "warning",
        skipped: "grey",
        info: "info",
        pending: "grey lighten-1",
      };
      return colors[status] || "grey";
    },

    getStepTextColor(status) {
      if (status === "warning") return "black";
      return "white";
    },

    getStepIcon(status) {
      const icons = {
        success: "mdi-check",
        failed: "mdi-close",
        warning: "mdi-alert",
        skipped: "mdi-skip-forward",
        info: "mdi-information",
        pending: "mdi-clock-outline",
      };
      return icons[status] || "mdi-circle";
    },

    getOverallAlertType() {
      if (!this.testResult) return "info";
      if (!this.testResult.success) return "error";
      if (this.testResult.authWarning) return "warning";
      return "success";
    },

    toggleStepDetails(stepNumber) {
      const idx = this.expandedSteps.indexOf(stepNumber);
      if (idx > -1) {
        this.expandedSteps.splice(idx, 1);
      } else {
        this.expandedSteps.push(stepNumber);
      }
    },

    getFailureReason() {
      if (!this.testResult || !this.testResult.steps) return null;
      const failedStep = this.testResult.steps.find(
        (s) => s.status === "failed"
      );
      return failedStep ? failedStep.message : null;
    },

    formatDate(dateString) {
      if (!dateString) return "Unknown";
      const date = new Date(dateString);
      return date.toLocaleString();
    },

    closeIfNotTesting() {
      if (!this.isTesting) {
        this.closeDialog();
      }
    },

    closeDialog() {
      this.dialogVisible = false;
      this.testResult = null;
      this.displaySteps = [];
      this.expandedSteps = [];
    },
  },
};
</script>

<style scoped>
.carrier-test-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
}

.test-steps {
  min-height: 200px;
}

.step-content {
  padding-bottom: 16px;
}

.step-title {
  font-size: 14px;
}

.step-message {
  font-size: 13px;
  line-height: 1.4;
}

.step-duration {
  font-size: 11px;
}

.step-details {
  max-height: 150px;
  overflow-y: auto;
}

.step-details pre {
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  font-size: 11px;
}

.v-timeline-item {
  padding-bottom: 8px !important;
}

.v-timeline::before {
  height: calc(100% - 24px) !important;
}
</style>
